import { NgIf, NgStyle } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ElementRef,
  Renderer2,
  ViewChild,
  forwardRef
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import {
  isJsonControlValueValid,
  normalizeJsonEditorText,
  normalizeWrittenValue,
  shouldPropagateParsedJson
} from './json-editor.utils';

interface IFormatting {
  number?: string;
  braces?: string;
  brackets?: string;
  colon?: string;
  comma?: string;
  string?: string;
  string_quotes?: string;
  key?: string;
  key_quotes?: string;
  null?: string;
  true?: string;
  false?: string;
}

@Component({
  selector: 'lite-json-editor',
  standalone: true,
  templateUrl: './json-editor.component.html',
  styleUrls: ['./json-editor.component.less'],
  imports: [NgStyle, NgIf],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LiteJsonEditorComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => LiteJsonEditorComponent),
      multi: true
    }
  ]
})
export class LiteJsonEditorComponent
  implements OnInit, ControlValueAccessor, Validator
{
  @ViewChild('editor', { static: true }) editorDiv: ElementRef<HTMLDivElement>;

  @Input() modelValue: any;
  @Input() indent: number = 2;
  @Input() dark: boolean = false;
  @Input() formatting: IFormatting;
  @Input() withoutEdit = false;
  @Input() withoutError = false;

  defaultFormats: IFormatting = {
    number: '#0a0cff',
    braces: '#84aecc',
    brackets: '#d26a6a',
    colon: this.dark ? '#ffffff' : '#4f4f4f',
    comma: this.dark ? '#ffff25' : '#000',
    string: this.dark ? '#78dce8' : '#5f9fca',
    string_quotes: '#5f9fca',
    key: '#e34c00',
    key_quotes: '#e34c00',
    null: '#cccccc',
    true: this.dark ? '#c2e69f' : '#569f41',
    false: '#d30000'
  };

  part: IFormatting;

  // ControlValueAccessor methods
  private onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};
  /** True after emitting an invalid JSON string; cleared on next successful parse emit. */
  private lastEmittedWasInvalid = false;
  /** Last value written by the form, before any editability-dependent normalization. */
  private rawValue: any = {};

  validate(control: AbstractControl): ValidationErrors | null {
    // skip the validation if withoutError is true
    if (this.withoutError) {
      return null;
    }

    return isJsonControlValueValid(control.value, this.lastEmittedWasInvalid)
      ? null
      : { jsonInvalid: true };
  }

  writeValue(value: any): void {
    this.rawValue = value;
    this.lastEmittedWasInvalid = false;
    this.renderValue();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.withoutEdit = isDisabled;
    // Editability changes how empty values render, so re-render on toggle.
    this.renderValue();
  }

  /**
   * Render {@link rawValue}. Editable editors normalize null/undefined to {} so
   * the user can start adding keys; read-only editors must not fabricate an empty
   * object for genuinely absent data, so they render it as-is (empty).
   */
  private renderValue(): void {
    const value = normalizeWrittenValue(this.rawValue, this.withoutEdit);
    this.modelValue = value;
    if (this.editorDiv?.nativeElement) {
      this.updateEditorContent(this.formatInput(value));
    }
  }

  constructor(private el: ElementRef, private renderer: Renderer2) {
    this.part = this.formatting
      ? Object.keys(this.defaultFormats).reduce(
          (p, c) => ({
            ...p,
            [c]: this.formatting[c] ?? this.defaultFormats[c]
          }),
          {}
        )
      : this.defaultFormats;
  }

  ngOnInit() {
    // Initialize if needed
  }

  onEditorInput(event: any) {
    const target = event.target;
    const formatted = normalizeJsonEditorText(target.innerText || '');

    try {
      const current = formatted ? JSON.parse(formatted) : {};
      const modelChanged =
        JSON.stringify(current) !== JSON.stringify(this.modelValue);
      const shouldPropagate = shouldPropagateParsedJson(
        current,
        this.modelValue,
        this.lastEmittedWasInvalid
      );

      if (!this.withoutError) {
        const errorPopup = this.el.nativeElement.querySelector('.error-popup');
        errorPopup.style.display = 'none';
      }

      if (shouldPropagate) {
        // Reformat only when the parsed object actually changed; recovering
        // from an invalid string with the same object must still call onChange
        // so the FormControl drops the stale invalid string (CAR-1899).
        if (modelChanged) {
          const pointer = this.getCaretPointer(target);
          this.modelValue = current;
          this.updateEditorContent(this.formatInput(current));
          if (pointer) {
            setTimeout(() => {
              this.setCaretFromPointer(target, pointer);
            });
          }
        } else {
          this.modelValue = current;
        }
        this.lastEmittedWasInvalid = false;
        this.onChange(current);
      }
    } catch {
      if (!this.withoutError) {
        const errorPopup = this.el.nativeElement.querySelector('.error-popup');
        errorPopup.style.display = 'block';
        this.lastEmittedWasInvalid = true;
        this.onChange(formatted);
      }
    }
  }

  /** @deprecated Prefer onEditorInput; kept for any external keyup bindings. */
  onKeyUp(event: any) {
    this.onEditorInput(event);
  }
  getCaretPointer(target: any) {
    const selection = document.getSelection();

    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const caretRange = range.cloneRange();

      caretRange.selectNodeContents(target);
      caretRange.setEnd(range.endContainer, range.endOffset);

      const section = caretRange.toString();
      const character = section[section.length - 1];
      const occurrence = this.getNumberOfOccurrences(section, character);

      return { character, occurrence, section };
    }

    return null;
  }

  setCaretFromPointer(target, pointer) {
    const selection = window.getSelection();
    const range = document.createRange();
    let nodesToExplore = this.getTextNodes(target);
    let occurrence = pointer.occurrence;
    let fount_at = 0,
      node;

    for (let i = 0; i < nodesToExplore.length; i++) {
      node = nodesToExplore[i];

      fount_at = this.getPositionOfOccurrence(
        node.textContent,
        pointer.character,
        occurrence
      );

      if (fount_at >= 0) break;

      occurrence -= this.getNumberOfOccurrences(
        node.textContent,
        pointer.character
      );
    }

    fount_at++;
    range.setStart(node, fount_at);
    range.setEnd(node, fount_at);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  getTextNodes(element) {
    let node,
      list = [],
      walk = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

    while ((node = walk.nextNode())) list.push(node);

    return list;
  }

  getPositionOfOccurrence = (string, sub_string, occurrence) => {
    const position = string
      .split(sub_string, occurrence)
      .join(sub_string).length;
    return position === string.length ? -1 : position;
  };

  escapeRegexString(string: string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  getNumberOfOccurrences(string: string, sub_string: string) {
    return sub_string
      ? string.replace(
          new RegExp(`[^${this.escapeRegexString(sub_string)}]`, 'g'),
          ''
        ).length
      : 0;
  }

  formatInput(input: string | Array<any> | Object, offset = 0) {
    const type = Array.isArray(input) ? 'array' : typeof input;

    switch (type) {
      case 'object':
        return this.formatObject(input as any, offset);
      case 'array':
        return this.formatArray(input as Array<any>, offset);
      case 'string':
        return this.formatString(input as string);
      case 'boolean':
        return this.formatBoolean(input as boolean);
      case 'number':
        return this.formatNumber(input as number);
      default:
        return input.toString();
    }
  }

  formatObject = (input: Object, offset = 0) => {
    if (input === null)
      return `<span style="font-family: monospace; color: ${this.part.null}">null</span>`;

    let output = '';

    if (Object.keys(input).length === 0) {
      return `<span style="font-family: monospace; color: ${this.part.braces}">{}</span>\n`;
    }

    output += `<span style="font-family: monospace; color: ${this.part.braces}">{</span>\n`;

    Object.keys(input).forEach(
      (key, index, list) =>
        (output += `${'&nbsp;'.repeat(
          offset + this.indent
        )}<span style="font-family: monospace; color: ${
          this.part.key
        }"><span style="font-family: monospace; color: ${
          this.part.key_quotes
        }">"</span>${key}<span style="font-family: monospace; color: ${
          this.part.key_quotes
        }">"</span></span><span style="font-family: monospace; color: ${
          this.part.colon
        }">:</span>${this.formatInput(input[key], offset + this.indent)}${
          index < list.length - 1
            ? `<span style="font-family: monospace; color: ${this.part.comma}">,</span>`
            : ''
        }\n`)
    );

    output += '&nbsp;'.repeat(offset);
    output += `<span style="font-family: monospace; color: ${this.part.braces}">}</span>`;

    return output;
  };

  formatArray = (input: Array<any>, offset = 0) => {
    let output = '';

    if (input.length === 0) {
      return `<span style="font-family: monospace; color: ${this.part.brackets}">[]</span>\n`;
    }

    output += `<span style="font-family: monospace; color: ${this.part.brackets}">[</span>\n`;

    input.forEach(
      (value, index, list) =>
        (output += `${'&nbsp;'.repeat(
          offset + this.indent
        )}<span>${this.formatInput(value, offset + this.indent)}</span>${
          index < list.length - 1
            ? `<span style="font-family: monospace; color: ${this.part.comma}">,</span>`
            : ''
        }\n`)
    );

    output += '&nbsp;'.repeat(offset);
    output += `<span style="font-family: monospace; color: ${this.part.brackets}">]</span>`;

    return output;
  };

  formatString(input: string) {
    return `<span style="font-family: monospace; color: ${this.part.string}"><span style="font-family: monospace; color: ${this.part.string_quotes}">"</span>${input}<span style="font-family: monospace; color: ${this.part.string_quotes}">"</span></span>`;
  }

  formatBoolean(input: boolean) {
    return `<span style="font-family: monospace; color: ${
      this.part[input.toString()]
    }">${input.toString()}</span>`;
  }

  formatNumber(input) {
    return `<span style="font-family: monospace; color: ${this.part.number}">${input}</span>`;
  }

  updateEditorContent(htmlContent: string): void {
    this.renderer.setProperty(
      this.editorDiv.nativeElement,
      'innerHTML',
      htmlContent
    );
  }
}
